import { Briefcase, Building2, CircleCheck, Clock, ExternalLink, Lock, Mail, MapPin, MapPinOff } from "lucide-react";
import { RegulatorBadge, regulatorShortName } from "@/components/regulator-badge";

/* Read-only facility profile (UX Revision 2e): a map cover banner with the facility tile overlapping it,
   name + identifiers + address, a strip of up to four key facts, then a few quiet detail lines.
   Values carry their meaning; captions appear only where a bare number would be ambiguous. */

type FacilityRecord = Record<string, string | undefined>;

const join = (parts: (string | undefined)[], separator = ", ") => parts.map((part) => part?.trim()).filter(Boolean).join(separator);
const isOn = (value: string | undefined) => value === "1" || value === "true" || value === "Yes";
const present = (value: string | undefined) => Boolean(value?.trim()) && value !== "0";

const hourFlags: [string, string][] = [
  ["open_whole_day", "Open 24 hours"], ["open_weekends", "Weekends"], ["open_public_holiday", "Public holidays"], ["open_late_night", "Late night"],
];

export function coordinatesOf(facility: FacilityRecord) {
  const lat = Number(facility.latitude);
  const lon = Number(facility.longitude);
  const valid = facility.latitude?.trim() && facility.longitude?.trim() && Number.isFinite(lat) && Number.isFinite(lon) && Math.abs(lat) <= 90 && Math.abs(lon) <= 180 && !(lat === 0 && lon === 0);
  return valid ? { lat, lon } : null;
}

export const mapLinkFor = ({ lat, lon }: { lat: number; lon: number }) => `https://www.openstreetmap.org/?mlat=${lat.toFixed(5)}&mlon=${lon.toFixed(5)}#map=16/${lat.toFixed(5)}/${lon.toFixed(5)}`;

/* Map cover: an OpenStreetMap embed (no API key, no client library). It is decorative here and not
   interactive; "Open in maps" next to the address is the way to explore. */
export function MapCover({ facility, label }: { facility: FacilityRecord; label: string }) {
  const point = coordinatesOf(facility);
  if (!point) return <div className="profile-cover empty"><span><MapPinOff size={16} aria-hidden="true" />Location not on record in the registry</span></div>;
  const { lat, lon } = point;
  const bbox = [lon - 0.012, lat - 0.006, lon + 0.012, lat + 0.006].map((value) => value.toFixed(5)).join(",");
  const src = `https://www.openstreetmap.org/export/embed.html?bbox=${bbox}&layer=mapnik&marker=${lat.toFixed(5)},${lon.toFixed(5)}`;
  return <div className="profile-cover">
    <iframe title={`Map showing ${label}`} src={src} tabIndex={-1} referrerPolicy="no-referrer-when-downgrade" />
    {/* The embed's own controls and attribution bar are cropped; attribution is shown here instead (ODbL). */}
    <a className="profile-cover-credit" href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener noreferrer">© OpenStreetMap contributors</a>
  </div>;
}

/* `variant="compact"` keeps the head and the footer (plus the map cover when `map` asks for it): no facts strip or detail lines.
   `footerAction` adds a link or note on the right of the footer. `regulator` fills in the
   regulatory body when the record passed in does not hold it. */
export function FacilitySummary({ facility, map = "auto", variant = "full", footerAction, regulator: regulatorOverride }: { facility: FacilityRecord; map?: "auto" | "always" | "never"; variant?: "full" | "compact"; footerAction?: React.ReactNode; regulator?: string }) {
  const name = facility.facility_name || "Your facility";
  const regulator = facility.regulatory_body?.trim() || regulatorOverride?.trim() || "";
  const point = coordinatesOf(facility);
  const compact = variant === "compact";
  const showCover = map === "always" || (map === "auto" && point);
  // Labelled identifiers under the name: the facility FID and the registration number.
  const identifiers = ([["Facility FID", facility.facility_fid || facility.hie_id], ["Registration number", facility.registration_number]] as [string, string | undefined][])
    .filter(([, value]) => present(value)) as [string, string][];
  const address = join([facility.address || facility.physical_address, facility.ward, facility.sub_county, facility.county]);
  const status = facility.operational_status;

  const factCandidates: [string | undefined, string][] = [
    [facility.facility_type, "Facility type"],
    [facility.facility_level || facility.keph_level, "KEPH level"],
    [facility.number_of_beds, "Beds"],
    [facility.maximum_bed_allocation, "Max bed allocation"],
    [facility.facility_owner_type, "Ownership"],
  ];
  const facts = factCandidates.filter(([value]) => present(value)).slice(0, 4) as [string, string][];
  const ownershipInFacts = facts.some(([, caption]) => caption === "Ownership");
  const owner = ownershipInFacts ? facility.facility_owner : join([facility.facility_owner, facility.facility_owner_type], " · ");
  const hasHours = hourFlags.some(([key]) => facility[key] !== undefined && facility[key] !== "");
  const hours = hourFlags.filter(([key]) => isOn(facility[key])).map(([, label]) => label);

  return <section className={`profile-card${showCover ? " with-cover" : ""}${compact ? " compact" : ""}`} aria-labelledby="facility-summary-title">
    {showCover && <MapCover facility={facility} label={name} />}
    <header className="profile-head">
      <span className="profile-tile" aria-hidden="true"><Building2 size={22} /></span>
      <div className="profile-title">
        <div className="profile-name-row">
          <h2 id="facility-summary-title">{name}</h2>
          <span className="auth-badge success"><CircleCheck size={12} />Verified</span>
        </div>
        {regulator && <RegulatorBadge name={regulator} />}
        {identifiers.length > 0 && <dl className="profile-ids">{identifiers.map(([label, value]) => <div key={label}><dt>{label}</dt><dd>{value}</dd></div>)}</dl>}
        {address && <p className="profile-address"><MapPin size={14} aria-hidden="true" /><span><span className="sr-only">Address: </span>{address}
          {point && <> <a href={mapLinkFor(point)} target="_blank" rel="noopener noreferrer">Open in maps <ExternalLink size={12} aria-hidden="true" /></a></>}</span></p>}
      </div>
    </header>

    {!compact && facts.length > 0 && <dl className={`profile-facts count-${facts.length}`}>
      {facts.map(([value, caption]) => <div key={caption}><dt>{caption}</dt><dd>{value}</dd></div>)}
    </dl>}

    {!compact && (present(facility.email) || present(owner) || hasHours) && <ul className="profile-lines">
      {present(facility.email) && <li><Mail size={16} aria-hidden="true" /><span className="sr-only">Facility email: </span><span>{facility.email!.split("@").map((part, index) => index ? <span key={index}>@<wbr />{part}</span> : part)}</span></li>}
      {present(owner) && <li><Briefcase size={16} aria-hidden="true" /><span className="sr-only">Owner: </span><span>{owner}</span></li>}
      {hasHours && <li><Clock size={16} aria-hidden="true" /><span className="sr-only">Operating hours: </span><span>{hours.length ? hours.join(" · ") : "Standard hours"}</span></li>}
    </ul>}

    <footer className="profile-foot">
      <span><Lock size={12} aria-hidden="true" />From the {regulator ? `${regulatorShortName(regulator)} ` : "facility "}registry · read-only</span>
      {status && <span className={`profile-status${/operational|active/i.test(status) ? " positive" : ""}`}><span aria-hidden="true" />{status}</span>}
      {footerAction && <span className="profile-foot-action">{footerAction}</span>}
    </footer>
  </section>;
}
